/**
 * Assessment context for managing assessment state
 */
import React, { createContext, useContext, useReducer, ReactNode } from 'react';
import { User, Question, Response, Assessment, MaturityLevel } from '../models';

// Define context state type
interface AssessmentState {
  user: User | null;
  assessment: Assessment | null;
  currentQuestionIndex: number;
  responses: Record<string, number>; // questionId -> responseValue
  isSubmitting: boolean;
  error: string | null;
}

// Define action types
type AssessmentAction =
  | { type: 'SET_USER'; payload: User }
  | { type: 'SET_ASSESSMENT'; payload: Assessment }
  | { type: 'SET_CURRENT_QUESTION'; payload: number }
  | { type: 'SET_RESPONSE'; payload: { questionId: string; value: number } }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_SUCCESS'; payload: Assessment }
  | { type: 'SUBMIT_ERROR'; payload: string }
  | { type: 'RESET' };

// Define initial state
const initialState: AssessmentState = {
  user: null,
  assessment: null,
  currentQuestionIndex: 0,
  responses: {},
  isSubmitting: false,
  error: null,
};

// Create context
const AssessmentContext = createContext<{
  state: AssessmentState;
  dispatch: React.Dispatch<AssessmentAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Create reducer
function assessmentReducer(state: AssessmentState, action: AssessmentAction): AssessmentState {
  switch (action.type) {
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_ASSESSMENT':
      return { ...state, assessment: action.payload };
    case 'SET_CURRENT_QUESTION':
      return { ...state, currentQuestionIndex: action.payload };
    case 'SET_RESPONSE':
      return {
        ...state,
        responses: {
          ...state.responses,
          [action.payload.questionId]: action.payload.value,
        },
      };
    case 'SUBMIT_START':
      return { ...state, isSubmitting: true, error: null };
    case 'SUBMIT_SUCCESS':
      return { ...state, isSubmitting: false, assessment: action.payload };
    case 'SUBMIT_ERROR':
      return { ...state, isSubmitting: false, error: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// Create provider component
export function AssessmentProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState);

  return (
    <AssessmentContext.Provider value={{ state, dispatch }}>
      {children}
    </AssessmentContext.Provider>
  );
}

// Custom hook for using the assessment context
export function useAssessmentContext() {
  const context = useContext(AssessmentContext);
  if (!context) {
    throw new Error('useAssessmentContext must be used within an AssessmentProvider');
  }
  return context;
}
